/**
 * Dashboard Header
 * Responsive header with hamburger menu for mobile
 */

import { useCallback, useMemo } from 'react'

import { useDispatch, useTranslation, useSelector } from '@ufc/core'
import { useTheme, useSidebar } from '@/providers'
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LogOut,
} from '@ufc/uikit'
import {
  ThemeToggle,
  LanguageToggle,
  UserProfile,
  MobileUserAvatar,
  MobileLogo,
  MobileMenuButton,
} from './components'
import { isRtlLanguage } from '@ufc/portal-shared'
import { selectIsSuperAdmin } from '@/lib/auth'
import { isSupportedLanguage, persistLanguage } from '@/lib/language-preferences'

import { authActions, selectAuthUser } from '@ufc/domains/auth'

// ============================================
// Helper Functions
// ============================================

function getUserDisplayName(user: { username?: string; email?: string } | null, isArabic: boolean): string {
  if (!user) return isArabic ? 'ضيف' : 'Guest'
  if (user.username) return user.username
  if (user.email) return user.email.split('@')[0]
  return isArabic ? 'مستخدم' : 'User'
}

function logout(dispatch: ReturnType<typeof useDispatch>): void {
  dispatch(authActions.logout())
}

// ============================================
// Main Component
// ============================================

export function DashboardHeader() {
  const { t, i18n } = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const { openMobile, isMobile } = useSidebar()
  const dispatch = useDispatch()
  const user = useSelector(selectAuthUser)
  const isSuperAdmin = useSelector(selectIsSuperAdmin)
  const isRTL = isRtlLanguage(i18n.language)

  const handleLanguageChange = useCallback((language: string) => {
    if (!isSupportedLanguage(language)) return

    void i18n.changeLanguage(language)
    persistLanguage(language)
  }, [i18n])
  const handleLogout = useCallback(() => logout(dispatch), [dispatch])

  const userName = useMemo(() => getUserDisplayName(user, isRTL), [user, isRTL])
  const showMobileMenuButton = isMobile && isSuperAdmin

  return (
    <header
      className={cn(
        'glass-strong rounded-2xl px-4 md:px-6 flex items-center justify-between gap-4',
        'h-16 md:h-20 mx-4 mt-4'
      )}
    >
      {/* Left section */}
      <div className="flex items-center gap-3 flex-1">
        {showMobileMenuButton && <MobileMenuButton onClick={openMobile} />}
        {isMobile && <MobileLogo />}
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2 md:gap-3">
        <ThemeToggle
          theme={theme}
          onToggle={toggleTheme}
          isMobile={isMobile}
        />

        <LanguageToggle
          currentLang={i18n.language}
          onChange={handleLanguageChange}
          isMobile={isMobile}
        />

        {/* User Menu with Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="focus:outline-none">
              {!isMobile ? (
                <UserProfile
                  name={userName}
                  role={t('header.kiosk')}
                  isRTL={isRTL}
                />
              ) : (
                <MobileUserAvatar />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{userName}</p>
              {user?.email && (
                <p className="text-xs text-muted-foreground">{user.email}</p>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="me-2 h-4 w-4" />
              {t('header.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

// Re-export types
export type * from './types'
